// 261010 P9D D9 — «Công cụ plan» (P8f) trong khung 8 bước: drawer 5 tab = Danh sách plan · Chạy & chấm · Trợ lý AI · Kiểu prompt · Kết quả chấm.
// P8f TRỪ thẻ «Chạy chặng 1/2» (I7): chặng 1 chạy bằng nút «Phân đoạn» ở bước 1, không nhân đôi runner L1; không nút pass lẻ / «Run remaining».
// Route CŨ (không bọc sổ L1, như Phase doc câu hỏi 4): GET lint · POST judge · chatbot-suggest/health-check · PUT batch-edit · PUT prompt-mode. Không hiện giá.
import { needsConfirm } from './workflowConfirmPolicy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Modal, Spin, Tabs, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { BatchEditOp, EventPlan, FilmPromptMode, FilmRunningJob } from '../../types/film';
import type { WorkflowStepKey } from '../../types/filmWorkflow';
import { apiErrorText } from './apiErrorText';
import FilmChatPanel, { type ChatMsg } from './FilmChatPanel';
import {
  PROMPT_MODE_VI, engineRowsOf, verifyRetryOf, chatStageLabelOf, type VerifyKind, judgeBars, promptModeImpactText, toolsApplyBlockReason,
} from './planToolsUtils';

interface Props {
  open: boolean;
  onClose: () => void;
  planId: string;
  jobs: FilmRunningJob[];
  onOpenPlans: () => void;
  onGoStep: (step: WorkflowStepKey) => void;
  onActionDone: () => void;
  /** Bước đang xem (bảng hoặc chi tiết video) — ngữ cảnh cho trợ lý (CH5). */
  currentStep?: WorkflowStepKey | null;
}

type Lint = Awaited<ReturnType<typeof filmApi.lintPlan>>;
type Judge = Awaited<ReturnType<typeof filmApi.judgePlan>>;
type Verify = { lint: Lint | null; judge: Judge | null; at: Date; rounds?: number };

const WorkflowPlanTools: React.FC<Props> = ({ open, onClose, planId, jobs, onOpenPlans, onGoStep, onActionDone, currentStep }) => {
  const [tab, setTab] = useState('run');
  const [plan, setPlan] = useState<EventPlan | null>(null);
  const [planErr, setPlanErr] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [verify, setVerify] = useState<Verify | null>(null);
  const [linting, setLinting] = useState(false);
  const [judging, setJudging] = useState(false);
  const [verifyErr, setVerifyErr] = useState<{ text: string; kind: VerifyKind } | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [modeBusy, setModeBusy] = useState(false);
  const [modeErr, setModeErr] = useState<string | null>(null);
  const [modeNote, setModeNote] = useState<string | null>(null);

  const loadPlan = useCallback(async () => {
    setLoadingPlan(true); setPlanErr(null);
    try { setPlan(await filmApi.getEventPlan(planId)); }
    catch (e) { setPlanErr(apiErrorText(e, 'Tải plan')); }
    finally { setLoadingPlan(false); }
  }, [planId]);
  useEffect(() => { if (open && !plan && !loadingPlan) void loadPlan(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPlan(null); setVerify(null); setMessages([]); setModeNote(null); }, [planId]);

  const events = useMemo(() => plan?.scenes.flatMap((s) => s.events) ?? [], [plan]);
  const applyBlock = toolsApplyBlockReason(jobs);

  // ── Kiểm lỗi (code, tức thì, 0 AI) / Chấm AI (lint + judge như UI cũ) ──
  const runLint = async () => {
    setLinting(true); setVerifyErr(null);
    try { const lint = await filmApi.lintPlan(planId); setVerify((v) => ({ lint, judge: v?.judge ?? null, at: new Date() })); setTab('result'); }
    catch (e) { setVerifyErr({ text: apiErrorText(e, 'Kiểm lỗi'), kind: 'lint' }); }
    finally { setLinting(false); }
  };
  const doJudge = async () => {
    setJudging(true); setVerifyErr(null);
    try {
      const [lint, judge] = await Promise.all([filmApi.lintPlan(planId), filmApi.judgePlan(planId)]);
      setVerify({ lint, judge, at: new Date(), rounds: judge.rounds }); setTab('result');
    } catch (e) { setVerifyErr({ text: apiErrorText(e, 'Chấm plan'), kind: 'judge' }); }
    finally { setJudging(false); }
  };
  // U9: chấm plan = một lần chạy AI chữ (không phải clip video) ⇒ chạy ngay.
  const askJudge = () => (!needsConfirm(null, 'RUN') ? doJudge() : Modal.confirm({
    title: 'Chấm plan bằng AI?', okText: 'Chấm', cancelText: 'Huỷ',
    content: 'Máy kiểm bằng code rồi nhờ AI chấm 5 tiêu chí (chỉ AI chữ — không tạo ảnh hay video). Có thể mất vài chục giây. Kết quả chỉ giữ trong phiên này, không lưu vào plan.',
    onOk: () => doJudge(),
  }));

  // ── Trợ lý: áp đề xuất thẳng vào plan (batch-edit) ──
  const applyOps = async (ops: BatchEditOp[]): Promise<number> => {
    await filmApi.batchEditPlan(planId, ops);
    await loadPlan();
    return ops.length;
  };

  // ── Kiểu prompt cả plan: xem trước (dryRun) → xác nhận → đổi ──
  const currentMode: FilmPromptMode = (plan?.plan.promptMode as FilmPromptMode) || 'NATURAL';
  const otherMode: FilmPromptMode = currentMode === 'NATURAL' ? 'TECHNICAL' : 'NATURAL';
  const changeMode = async () => {
    setModeBusy(true); setModeErr(null);
    try {
      const preview = await filmApi.setPlanPromptMode(planId, otherMode, true);
      Modal.confirm({
        title: `Đổi cả plan sang kiểu ${PROMPT_MODE_VI[otherMode]}?`, okText: 'Đổi kiểu', cancelText: 'Huỷ',
        content: promptModeImpactText(otherMode, preview),
        onOk: async () => {
          try {
            await filmApi.setPlanPromptMode(planId, otherMode);
            setModeNote(`Đã đổi plan sang kiểu ${PROMPT_MODE_VI[otherMode]}. ${preview.mismatchAfter.length ? `${preview.mismatchAfter.length} prompt đang lệch kiểu — dựng lại bằng nút chạy của bước 7 — Prompt.` : 'Không prompt nào bị lệch.'}`);
            await loadPlan(); onActionDone();
          } catch (e) { setModeErr(apiErrorText(e, 'Đổi kiểu prompt')); }
        },
      });
    } catch (e) { setModeErr(apiErrorText(e, 'Xem trước đổi kiểu prompt')); }
    finally { setModeBusy(false); }
  };

  const rows = useMemo(() => engineRowsOf(events, plan?.plan.engine === 'BYTEPLUS' ? null : (events.find((e) => e.videoEngine)?.videoEngine ?? null)), [events, plan]);

  const planBody = (children: React.ReactNode) => (loadingPlan && !plan ? <div style={{ padding: 16 }}><Spin size="small" /> <span className="muted">Đang tải plan…</span></div>
    : planErr && !plan ? <Alert type="warning" showIcon message={planErr} action={<Button size="small" onClick={() => { void loadPlan(); }}>Tải lại</Button>} /> : children);

  const items = [
    {
      key: 'plans', label: 'Danh sách plan', children: (
        <div data-testid="wf-tools-plans">
          <p className="muted">Mở danh sách plan của truyện để chuyển plan hoặc tạo plan mới. Plan luồng mới mở khung 8 bước; plan kiểu cũ mở UI cũ.</p>
          <Button onClick={() => { onClose(); onOpenPlans(); }}>← Plans</Button>
        </div>
      ),
    },
    {
      key: 'run', label: 'Chạy & chấm', children: (
        <div data-testid="wf-tools-run">
          <p className="muted" style={{ marginTop: 0 }}>Chạy / chạy lại <b>Phân đoạn</b> (chặng 1) bằng nút «Phân đoạn» ở bước 1 — không chạy ở đây.</p>
          <Button size="small" onClick={() => { onClose(); onGoStep('SCRIPT'); }} data-testid="wf-tools-goto-script">Mở bước 1 — Phân đoạn</Button>
          <h3 style={{ marginTop: 14 }}>Kiểm lỗi plan</h3>
          <p className="muted" style={{ marginTop: 0 }}>Máy kiểm bằng code (tức thì, không AI, không tốn phí).</p>
          <Button loading={linting} disabled={judging} onClick={() => { void runLint(); }} data-testid="wf-tools-lint">Kiểm lỗi</Button>
          <h3 style={{ marginTop: 14 }}>Chấm bằng AI</h3>
          <p className="muted" style={{ marginTop: 0 }}>AI chấm 5 tiêu chí: chia video · phủ nguồn · thoại · trung thành nguồn · mạch truyện. Chỉ AI chữ.</p>
          <Button type="primary" loading={judging} disabled={linting} onClick={askJudge} data-testid="wf-tools-judge">Chấm AI</Button>
          {verifyErr ? (() => {
            const r = verifyRetryOf(verifyErr.kind);
            return <Alert style={{ marginTop: 10 }} type="error" showIcon message={verifyErr.text} data-testid="wf-tools-verify-error"
              action={<Button size="small" disabled={linting || judging} onClick={() => { if (r.action === 'RUN_LINT') void runLint(); else askJudge(); }}>{r.label}</Button>} />;
          })() : null}
        </div>
      ),
    },
    {
      key: 'chat', label: 'Trợ lý AI', children: planBody(plan ? (
        <div style={{ height: 'calc(100vh - 220px)' }} data-testid="wf-tools-chat">
          <FilmChatPanel planId={planId} view={plan} onApplyOps={() => 0} onActionDone={onActionDone} messages={messages} onMessagesChange={setMessages} currentStage={chatStageLabelOf(currentStep)}
            workflow={{
              onApplyOps: applyOps, applyBlock, onGoStep: (s) => { onClose(); onGoStep(s); },
              onRunVerify: (k) => { setTab('run'); if (k === 'lint') void runLint(); else askJudge(); },
            }} />
        </div>
      ) : null),
    },
    {
      key: 'mode', label: 'Kiểu prompt', children: planBody(plan ? (
        <div data-testid="wf-tools-mode">
          <p style={{ marginTop: 0 }}>Kiểu prompt của plan: <b>{PROMPT_MODE_VI[currentMode]}</b>. Plan mới mặc định Tự nhiên; vẫn đổi được.</p>
          <Button loading={modeBusy} onClick={() => { void changeMode(); }} data-testid="wf-tools-mode-change">Đổi sang {PROMPT_MODE_VI[otherMode]}…</Button>
          {modeNote ? <Alert style={{ marginTop: 10 }} type="success" showIcon message={modeNote} action={modeNote.includes('lệch kiểu') ? <Button size="small" onClick={() => { onClose(); onGoStep('PROMPT'); }} data-testid="wf-tools-goto-prompt">Mở bước 7 — Prompt</Button> : undefined} /> : null}
          {modeErr ? <Alert style={{ marginTop: 10 }} type="error" showIcon message={modeErr} closable onClose={() => setModeErr(null)} /> : null}
          <h3 style={{ marginTop: 14 }}>Engine · độ dài · kiểu prompt từng video</h3>
          {rows.length === 0 ? <p className="muted">Plan chưa có video — chạy Phân đoạn ở bước 1.</p> : (
            <table className="wf-table" style={{ width: '100%', fontSize: 12.5 }}>
              <thead><tr><th>Video</th><th>Engine</th><th>Dài</th><th>Kiểu</th><th>Prompt</th><th /></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.order}</td><td>{r.engineLabel}{r.mismatch ? <span className="wf-chip bad" style={{ marginLeft: 4 }}>lệch engine</span> : null}</td>
                  <td>{r.durationS != null ? `${r.durationS}s` : '—'}</td><td>{r.modeLabel}</td><td>{r.hasPrompt ? 'có' : 'chưa'}</td>
                  <td>{r.mismatch ? <Button size="small" onClick={() => { onClose(); onGoStep('SCRIPT'); }}>Mở bước 1 để chia</Button> : null}</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
      ) : null),
    },
    {
      key: 'result', label: verify?.judge ? `Kết quả ${verify.judge.overall}/5` : 'Kết quả chấm', children: (
        <div data-testid="wf-tools-result">
          <p className="wf-note" style={{ marginTop: 0 }}>Kết quả chỉ giữ trong phiên này — tải lại trang là mất, không lưu vào plan.</p>
          {!verify ? <p className="muted">Chưa kiểm / chấm lần nào trong phiên này. Mở tab «Chạy & chấm».</p> : (
            <>
              <p className="muted" style={{ fontSize: 12 }}>Lúc {verify.at.toLocaleTimeString('vi-VN')}{verify.rounds ? ` · ${verify.rounds} vòng giám khảo` : ''}</p>
              {verify.judge ? (
                <div data-testid="wf-tools-judge-result">
                  <h3>Điểm tổng {verify.judge.overall}/5</h3>
                  {judgeBars(verify.judge.scores).map((b) => (
                    <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0', fontSize: 12.5 }}>
                      <span style={{ width: 130 }}>{b.label}</span>
                      <span style={{ flex: 1, background: '#eee', borderRadius: 4, height: 8 }}><span style={{ display: 'block', width: `${b.pct}%`, height: 8, background: b.value >= 4 ? '#52c41a' : b.value >= 3 ? '#faad14' : '#cf1322', borderRadius: 4 }} /></span>
                      <b style={{ width: 28, textAlign: 'right' }}>{b.value}</b>
                    </div>))}
                  {Object.entries(verify.judge.flaws || {}).some(([, v]) => v.length) ? (
                    <><h3>Điểm yếu AI thấy</h3><ul className="wf-list">{Object.entries(verify.judge.flaws).flatMap(([k, v]) => v.map((f, i) => <li key={`${k}-${i}`}>{f}</li>))}</ul></>
                  ) : null}
                </div>
              ) : null}
              {verify.lint ? (
                <div data-testid="wf-tools-lint-result">
                  <h3>Kiểm lỗi · {verify.lint.counts.error} lỗi · {verify.lint.counts.warn} cảnh báo</h3>
                  {verify.lint.findings.length === 0 ? <p className="muted">Không có lỗi hay cảnh báo.</p> : (
                    <ul className="wf-list">{verify.lint.findings.slice(0, 20).map((f, i) => (
                      <li key={`${f.code}-${i}`}><span className={`wf-chip ${f.severity === 'error' ? 'bad' : 'warn'}`}>{f.severity === 'error' ? 'lỗi' : 'cảnh báo'}</span> <span className="mono faint">{f.ref}</span> {f.message}</li>))}
                    </ul>)}
                  {verify.lint.findings.length > 20 ? <p className="faint">… và {verify.lint.findings.length - 20} mục nữa.</p> : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Drawer title="Công cụ plan" open={open} onClose={onClose} width={560} destroyOnClose={false}>
      <div className="wf">
        <Tabs activeKey={tab} onChange={setTab} items={items} />
      </div>
    </Drawer>
  );
};

export default WorkflowPlanTools;
